import { useCallback } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { rememberPaymentFlow } from 'utilities/analytics/paymentFlow'
import { addOnlinePayForOtherInLoan, postGatewayPaymentOneItemInstallment } from 'services/easyPay'
import useGatewayHandoff from 'utilities/hooks/useGatewayHandoff'
import { goToGateway } from 'modules/gatewayHandoff'

export default function useSetPay(data?: any) {
    const { locked, isLocked, lock, hold, release } = useGatewayHandoff()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { isLoading, mutate } = useMutation({
        mutationFn: (payload: any) => {
            return postGatewayPaymentOneItemInstallment(payload)
        },
        onSuccess: (result, variables) => {
            // Settled out of the wallet: the backend paid every installment
            // itself and there is no bank to visit, so there is no
            // `payment_link` to follow and nothing for the callback page to
            // finish. Redirecting to an absent link is what turns a settled
            // payment into «مشکلی پیش آمده», and leaving a
            // `toBePaidTransactions` record behind would hand the callback page
            // work for a payment that is already done.
            if (result?.paid_from_wallet) {
                const paid: any[] = variables?.paymentItems ?? []
                release()
                posthog.capture('payment_wallet_paid', {
                    entry: 'loan_installment',
                    amount: paid.reduce(
                        (sum, item) =>
                            sum +
                            Number(item?.itemAmount ?? 0) +
                            Number(item?.commission ?? 0),
                        0
                    ),
                    items_count: paid.length,
                    box_id: paid[0]?.box_id ?? null,
                })
                toast.success('پرداخت از کیف پول انجام شد')
                // The loan page, the wallet balance and the payables list all
                // moved; none of them should be read from cache after this.
                queryClient.invalidateQueries()
                navigate(-1)
                return
            }
            localStorage.setItem(
                'toBePaidTransactions',
                JSON.stringify({
                    // `type` is what the gateway callback reports as
                    // `flow_type` on payment_succeeded / payment_failed, so it
                    // has to be the same string this screen tags the redirect
                    // with. It used to say «pay-for-other», one character away
                    // from the unrelated «pay-for-others» flow the callback
                    // branches on, which split one funnel across two names.
                    //
                    // The callback has no branch for this value and falls
                    // through to /funds — which is the right landing for a
                    // repayment — so the rename is behaviour-preserving.
                    type: 'loan_installment',
                    data: data ? data : [],
                    //@ts-ignore
                    // pk: result.cashin.transaction.pk,
                    //@ts-ignore
                    // gateway: 0,
                })
            )
            const items: any[] = variables?.paymentItems ?? []
            posthog.capture('payment_gateway_redirect', { flow_type: 'loan_installment' })
            rememberPaymentFlow({
                flow_type: 'loan_installment',
                amount: items.reduce(
                    (sum, item) =>
                        sum + Number(item?.itemAmount ?? 0) +
                        Number(item?.commission ?? 0),
                    0
                ),
                gateway: variables?.gateway?.uniqueCode ?? null,
                box_id: items[0]?.box_id ?? null,
                box_type: 'mnp',
            })
            // The button stays down while the browser is on its way to the
            // bank — see `useGatewayHandoff`.
            hold()
            goToGateway(result?.payment_link)
            toast.success('در حال انتقال به صفحه کارت به کارت')
        },
        onError: (error: any) => {
            release()
            // Surface the backend's specific message. An amount over the
            // کارت‌به‌کارت ceiling comes back as {"error": "سقف واریز ..."} — the
            // one thing the payer can act on — and «مشکلی پیش آمده» told
            // them nothing. Fall back to the generic toast when the body has no
            // string message (serializer errors are an object, network errors
            // have no body at all).
            const backendMessage = error?.response?.data?.error
            posthog.capture('payment_initiate_failed', {
                flow_type: 'loan_installment',
                error_message:
                    typeof backendMessage === 'string' ? backendMessage : null,
                status: error?.response?.status,
            })
            toast.error(
                typeof backendMessage === 'string' && backendMessage
                    ? backendMessage
                    : 'مشکلی پیش آمده!'
            )
        },
    })
    const pay = useCallback(
        (payload: any) => {
            // Refused by the ref, not by `isLoading`: a second tap in the same
            // tick lands before React has re-rendered `disabled`.
            if (isLocked()) return
            lock()
            mutate(payload)
        },
        [isLocked, lock, mutate]
    )
    return { mutate: pay, isLoading: isLoading || locked }
}
